// AssistantPanel.js — AI survey generation panel for the designer
//
// Provides a text input for natural language prompts and generates
// surveys via the survey-assistant API, injecting results directly
// into the designer canvas.
//
// Snapshots the current canvas state before generating and sends it
// as context so the AI can preserve existing questions while adding
// or modifying content per the user's prompt.
//
// Uses Server-Sent Events (SSE) via fetch POST to stream real-time
// progress from the pipeline.

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { fetchAssistantsEnabled } from '../utils/cloudApi';
import './AssistantPanel.css';

const ASSISTANT_API = process.env.REACT_APP_ASSISTANT_API_BASE || 'http://localhost:5050';
const ACCEPTED_FILE_TYPES = '.pdf,.docx,.txt';

const EXAMPLE_PROMPTS = [
    "Create a customer satisfaction survey with rating scale and open feedback",
    "Build an employee wellness check with yes/no branching",
    "Make a quick event registration form with name, email, and track preference",
];

// Stage labels for the progress display (text-only, no icons)
const STAGE_LABELS = {
    extracting: 'Extracting',
    starting: 'Starting',
    planning: 'Planning',
    generating: 'Generating',
    validating: 'Validating',
    retrying: 'Retrying',
    complete: 'Complete',
};

export default function AssistantPanel({ isOpen, onClose }) {
    const [prompt, setPrompt] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [elapsed, setElapsed] = useState(null);
    const [progress, setProgress] = useState(null); // { stage, message }
    const [canUndo, setCanUndo] = useState(false);
    const [diagnostics, setDiagnostics] = useState([]);
    const [sheetUrl, setSheetUrl] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [showSyntaxRef, setShowSyntaxRef] = useState(false);
    const abortRef = useRef(null);
    const fileInputRef = useRef(null);

    // Plan gate. The assistant spends LLM tokens and tokens cost money, so a
    // free account that has used up its surveys does not get to keep spending
    // them. Design: docs/tier-enforcement-method.md
    //
    // null means "not checked yet", so the panel does not flash a refusal
    // before the answer arrives. The check runs each time the panel opens, so
    // an account that upgrades gets the assistant back without a reload.
    const [planRefusal, setPlanRefusal] = useState(null);
    useEffect(() => {
        if (!isOpen) return undefined;
        let cancelled = false;
        (async () => {
            const { enabled, message } = await fetchAssistantsEnabled();
            if (cancelled) return;
            setPlanRefusal(enabled ? '' : (message || 'The assistants are not available on your current plan.'));
        })();
        return () => { cancelled = true; };
    }, [isOpen]);

    const cancelGeneration = useCallback(() => {
        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
        }
        setLoading(false);
        setProgress(null);
    }, []);

    const handleGenerate = useCallback(() => {
        if (!prompt.trim() || loading) return;

        setLoading(true);
        setResult(null);
        setElapsed(null);
        setCanUndo(false);
        setProgress({ stage: 'starting', message: 'Connecting to assistant…' });

        // Snapshot current canvas state for context
        const existingSurvey = window.__designerExportSurvey
            ? window.__designerExportSurvey()
            : null;

        const controller = new AbortController();
        abortRef.current = controller;

        const url = `${ASSISTANT_API}/generate-stream`;

        // Use fetch POST for SSE (allows sending body with existing survey)
        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                prompt: prompt.trim(),
                existing_survey: existingSurvey,
            }),
            signal: controller.signal,
        }).then(async (response) => {
            if (!response.ok) {
                const text = await response.text().catch(() => '');
                setResult({ type: 'error', message: `API error (${response.status}): ${text || 'Unknown'}` });
                setLoading(false);
                setProgress(null);
                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || ''; // Keep incomplete line in buffer

                let eventType = null;
                for (const line of lines) {
                    if (line.startsWith('event: ')) {
                        eventType = line.slice(7).trim();
                    } else if (line.startsWith('data: ') && eventType) {
                        const dataStr = line.slice(6);
                        try {
                            const data = JSON.parse(dataStr);
                            if (eventType === 'progress') {
                                setProgress({ stage: data.stage, message: data.message });
                            } else if (eventType === 'result') {
                                setElapsed(data.elapsed_seconds);
                                setProgress(null);
                                setLoading(false);
                                if (!data.success) {
                                    setResult({ type: 'error', message: data.error || 'Generation failed' });
                                    return;
                                }
                                // Inject into designer via the bridge
                                if (window.__designerLoadSurvey) {
                                    const loadResult = window.__designerLoadSurvey(data.survey);
                                    if (loadResult?.success) {
                                        const nodeCount = data.survey.questions?.length || 0;
                                        const edgeCount = data.survey.flow?.length || 0;
                                        const name = data.survey.survey_descriptor?.survey_name || 'Survey';
                                        setResult({
                                            type: 'success',
                                            message: `"${name}" loaded — ${nodeCount} nodes, ${edgeCount} edges`,
                                            validation: data.validation,
                                            attempts: data.attempts,
                                        });
                                        setCanUndo(true);
                                    } else {
                                        setResult({
                                            type: 'error',
                                            message: `Bridge error: ${loadResult?.error || 'Unknown error'}`,
                                        });
                                    }
                                } else {
                                    setResult({
                                        type: 'error',
                                        message: 'Designer bridge not available. Is the designer loaded?',
                                    });
                                }
                            } else if (eventType === 'error') {
                                setResult({ type: 'error', message: data.error || 'Generation failed' });
                                setLoading(false);
                                setProgress(null);
                            }
                        } catch { /* ignore parse errors */ }
                        eventType = null;
                    } else if (line === '') {
                        eventType = null;
                    }
                }
            }

            // If we exit the loop without a result, mark as done
            setLoading(false);
            setProgress(null);
        }).catch((err) => {
            if (err.name === 'AbortError') return; // User cancelled
            setResult({
                type: 'error',
                message: `Connection failed: ${err.message}`,
            });
            setLoading(false);
            setProgress(null);
        });

    }, [prompt, loading]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            handleGenerate();
        }
    };

    const handleExample = (example) => {
        setPrompt(example);
        setResult(null);
    };

    const handleUndo = useCallback(() => {
        window.dispatchEvent(new CustomEvent('wf-undo-request'));
        setCanUndo(false);
        setResult(prev => prev ? { ...prev, message: prev.message + ' (undone)' } : prev);
    }, []);

    // ── File import handler (Phase 3) ──────────────────────────────
    const handleImport = useCallback(() => {
        if (loading) return;

        setLoading(true);
        setResult(null);
        setElapsed(null);
        setCanUndo(false);
        setDiagnostics([]);
        setProgress({ stage: 'extracting', message: 'Preparing upload…' });

        const controller = new AbortController();
        abortRef.current = controller;

        const url = `${ASSISTANT_API}/ingest-stream`;
        let body;
        let headers = {};

        if (sheetUrl.trim()) {
            // Google Sheets path
            body = JSON.stringify({ sheet_id: sheetUrl.trim() });
            headers['Content-Type'] = 'application/json';
        } else if (selectedFile) {
            // File upload path
            body = new FormData();
            body.append('file', selectedFile);
        } else {
            setLoading(false);
            setProgress(null);
            return;
        }

        fetch(url, {
            method: 'POST',
            headers: headers['Content-Type'] ? headers : undefined,
            body,
            signal: controller.signal,
        }).then(async (response) => {
            if (!response.ok) {
                const text = await response.text().catch(() => '');
                setResult({ type: 'error', message: `API error (${response.status}): ${text || 'Unknown'}` });
                setLoading(false);
                setProgress(null);
                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                let eventType = null;
                for (const line of lines) {
                    if (line.startsWith('event: ')) {
                        eventType = line.slice(7).trim();
                    } else if (line.startsWith('data: ') && eventType) {
                        const dataStr = line.slice(6);
                        try {
                            const data = JSON.parse(dataStr);
                            if (eventType === 'progress') {
                                setProgress({ stage: data.stage, message: data.message });
                            } else if (eventType === 'result') {
                                setElapsed(data.elapsed_seconds);
                                setProgress(null);
                                setLoading(false);

                                // Store diagnostics if any
                                if (data.diagnostics && data.diagnostics.length > 0) {
                                    setDiagnostics(data.diagnostics);
                                }

                                if (!data.success) {
                                    setResult({ type: 'error', message: data.error || 'Import failed' });
                                    return;
                                }

                                // Inject into designer
                                if (window.__designerLoadSurvey) {
                                    const loadResult = window.__designerLoadSurvey(data.survey);
                                    if (loadResult?.success) {
                                        const nodeCount = data.survey.questions?.length || 0;
                                        const edgeCount = data.survey.flow?.length || 0;
                                        const name = data.survey.survey_descriptor?.survey_name || 'Imported Survey';
                                        const fieldsMsg = data.fields_extracted ? ` from ${data.fields_extracted} fields` : '';
                                        setResult({
                                            type: 'success',
                                            message: `"${name}" imported${fieldsMsg} — ${nodeCount} nodes, ${edgeCount} edges`,
                                            validation: data.validation,
                                            attempts: data.attempts,
                                        });
                                        setCanUndo(true);
                                    } else {
                                        setResult({ type: 'error', message: `Bridge error: ${loadResult?.error || 'Unknown'}` });
                                    }
                                } else {
                                    setResult({ type: 'error', message: 'Designer bridge not available.' });
                                }
                            } else if (eventType === 'error') {
                                if (data.diagnostics) setDiagnostics(data.diagnostics);
                                setResult({ type: 'error', message: data.error || 'Import failed' });
                                setLoading(false);
                                setProgress(null);
                            }
                        } catch { /* ignore parse errors */ }
                        eventType = null;
                    } else if (line === '') {
                        eventType = null;
                    }
                }
            }
            setLoading(false);
            setProgress(null);
        }).catch((err) => {
            if (err.name === 'AbortError') return;
            setResult({ type: 'error', message: `Connection failed: ${err.message}` });
            setLoading(false);
            setProgress(null);
        });
    }, [sheetUrl, selectedFile, loading]);

    const stageLabel = progress && (STAGE_LABELS[progress.stage] || progress.stage);

    return (
        <div className={`assistant-panel ${isOpen ? 'open' : ''}`}>
            <div className="assistant-panel-header">
                <h3>Survey Assistant</h3>
                <button className="assistant-panel-close" onClick={onClose} title="Close">
                    <X size={14} />
                </button>
            </div>

            <div className="assistant-panel-body">
                {planRefusal ? (
                    // The whole panel is replaced, not merely disabled. A
                    // greyed-out box invites the author to keep clicking it.
                    <p className="assistant-plan-refusal">{planRefusal}</p>
                ) : (
                <>
                <label>Describe Your Survey</label>
                <textarea
                    className="assistant-prompt-input"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={"Describe the survey you want to create or modify...\n\ne.g. Add a question about dietary preferences after Q3"}
                    disabled={loading}
                />

                {!loading ? (
                    <button
                        className="assistant-generate-btn"
                        onClick={handleGenerate}
                        disabled={!prompt.trim()}
                    >
                        Generate Survey
                    </button>
                ) : (
                    <button
                        className="assistant-generate-btn cancel"
                        onClick={cancelGeneration}
                    >
                        Cancel
                    </button>
                )}

                {loading && progress && (
                    <div className="assistant-progress">
                        <div className="assistant-progress-status">
                            <span className="assistant-progress-dot" />
                            <span className="assistant-progress-stage">
                                {stageLabel}
                            </span>
                        </div>
                        <div className="assistant-progress-message">
                            {progress.message}
                        </div>
                    </div>
                )}

                {result && (
                    <div
                        className={`assistant-result ${result.type}`}
                        onClick={() => setResult(null)}
                        title="Click to dismiss"
                    >
                        {result.type === 'success' ? 'Success: ' : 'Error: '}
                        {result.message}
                        {elapsed && (
                            <div className="assistant-result-detail">
                                Generated in {elapsed}s
                                {result.attempts && result.attempts > 1 && ` · ${result.attempts} passes`}
                                {result.validation && ` · ${result.validation.errors} errors, ${result.validation.warnings} warnings`}
                            </div>
                        )}
                    </div>
                )}

                {/* ── Undo button (independent of result banner) ── */}
                {canUndo && (
                    <button className="assistant-undo-btn" onClick={handleUndo}>
                        Undo Last Change
                    </button>
                )}

                {/* ── Diagnostics from import ── */}
                {diagnostics.length > 0 && (
                    <div className="assistant-diagnostics">
                        <div className="assistant-diagnostics-header">
                            {diagnostics.length} issue(s) in source document:
                        </div>
                        {diagnostics.map((d, i) => (
                            <div key={i} className="assistant-diagnostics-item">
                                {d.line ? `Line ${d.line}: ` : ''}{d.error}
                            </div>
                        ))}
                    </div>
                )}

                {/* ── Import from Document (Phase 3) ── */}
                {/* ── Specification Syntax Popup ── */}
                {showSyntaxRef && (
                    <div
                        className="assistant-syntax-overlay"
                        onClick={() => setShowSyntaxRef(false)}
                    >
                        <div
                            className="assistant-syntax-modal"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="assistant-syntax-modal-header">
                                <span className="assistant-syntax-modal-title">Specification Syntax</span>
                                <button
                                    className="assistant-syntax-modal-close"
                                    onClick={() => setShowSyntaxRef(false)}
                                    title="Close"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                            <pre className="assistant-syntax-content">{`Question text [type]

Numbering is optional:
  1. Question text [type]
  Question text [type]

Supported types:
  [text]       Free-form text
  [number]     Numeric response
  [choice]     Single-select (list below)
  [yes_no]     Binary yes/no
  [scale: 1-5] Numeric scale (any range)
  [file]       File upload
  [email]      Email address

Choice options (indented, dash-prefixed):
  Favorite color? [choice]
     - Red
     - Green
     - Blue

Optional questions:
  Comments? [text, optional]

Notes:
  • Blank lines between questions are fine
  • No branching syntax — add in Designer
  • Generates linear flow (Q1 → Q2 → … → End)`}</pre>
                        </div>
                    </div>
                )}

                {!loading && (
                    <div className="assistant-import-section">
                        <div className="assistant-import-header">
                            <label>Import from Document</label>
                            <button
                                className="assistant-syntax-link"
                                onClick={() => setShowSyntaxRef(true)}
                                title="View specification syntax reference"
                            >Syntax</button>
                        </div>

                        {/* File upload */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={ACCEPTED_FILE_TYPES}
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) {
                                    setSelectedFile(f);
                                    setSheetUrl('');
                                    setResult(null);
                                    setDiagnostics([]);
                                }
                            }}
                        />

                        <button
                            className="assistant-file-btn"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={loading}
                        >
                            {selectedFile ? selectedFile.name : 'Choose file (PDF, DOCX, TXT)…'}
                        </button>

                        {/* Google Sheets URL */}
                        <input
                            className="assistant-sheet-input"
                            type="text"
                            value={sheetUrl}
                            onChange={(e) => {
                                setSheetUrl(e.target.value);
                                if (e.target.value.trim()) setSelectedFile(null);
                                setResult(null);
                                setDiagnostics([]);
                            }}
                            placeholder="Or paste Google Sheet URL…"
                            disabled={loading}
                        />

                        <button
                            className="assistant-generate-btn"
                            onClick={handleImport}
                            disabled={!selectedFile && !sheetUrl.trim()}
                        >
                            Import Survey
                        </button>
                    </div>
                )}

                {!loading && !result && (
                    <>
                        <label>Try an Example</label>
                        {EXAMPLE_PROMPTS.map((ex, i) => (
                            <button
                                key={i}
                                className="assistant-example-btn"
                                onClick={() => handleExample(ex)}
                            >
                                {ex}
                            </button>
                        ))}
                    </>
                )}
                </>
                )}
            </div>
        </div>
    );
}
